import { useEffect, useState } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Bars3Icon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline'
import { useAuthStore } from '../../store/authStore'
import { UserRole } from '../../types/auth'
import { AdminApi } from '../../services/adminApi'

type AdminNavItem = {
  to: string
  label: string
  badgeKey?: 'warnings'
}

type AdminNavSection = {
  title: string
  items: AdminNavItem[]
}

const adminNavSections: AdminNavSection[] = [
  {
    title: 'Nội dung',
    items: [
      { to: '/admin/courses', label: 'Quản lý khóa học' },
      { to: '/admin/workshops', label: 'Quản lý Workshop' },
      { to: '/admin/videos', label: 'Thư viện Video' },
      { to: '/admin/posts', label: 'Quản lý bài viết' },
      { to: '/admin/skills', label: 'Thư viện' },
    ],
  },
  {
    title: 'Học viên & Đăng ký',
    items: [
      { to: '/admin/users', label: 'Quản lý user' },
      { to: '/admin/registrations', label: 'Danh sách đăng ký' },
      { to: '/admin/enterprise-batches', label: 'Lô đăng ký DN' },
      { to: '/admin/workshop-registrations', label: 'Đăng ký Workshop' },
      { to: '/admin/reviews', label: 'Quản lý đánh giá' },
    ],
  },
  {
    title: 'Bài tập',
    items: [
      { to: '/admin/homework-submissions', label: 'Quản lý bài làm học viên' },
    ],
  },
  {
    title: 'Kiểm tra',
    items: [
      { to: '/admin/question-bank', label: 'Ngân hàng câu hỏi' },
      { to: '/admin/tests', label: 'Quản lý bài test' },
      { to: '/admin/test-results', label: 'Quản lý kết quả test' },
    ],
  },
  {
    title: 'Hệ thống',
    items: [
      { to: '/admin/warnings', label: 'Cảnh báo', badgeKey: 'warnings' },
      { to: '/admin/misa-invoice-logs', label: 'Hóa đơn MISA' },
    ],
  },
]

const SIDEBAR_PREF_KEY = 'admin-sidebar-collapsed'

const AdminLayout = () => {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const isAdmin = Boolean(user?.roles?.includes(UserRole.ADMIN))
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [warningCount, setWarningCount] = useState(0)

  useEffect(() => {
    const stored = window.localStorage.getItem(SIDEBAR_PREF_KEY)
    if (stored === '1') setCollapsed(true)
  }, [])

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_PREF_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    const loadBadge = async () => {
      try {
        const res = await AdminApi.getSecurityWarnings({ page: 1, limit: 1 })
        if (!cancelled) setWarningCount(res.data.unresolvedCount || 0)
      } catch {
        if (!cancelled) setWarningCount(0)
      }
    }
    void loadBadge()
    const timer = window.setInterval(() => {
      void loadBadge()
    }, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [isAdmin, location.pathname])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  const renderNavLink = (item: AdminNavItem, compact: boolean) => {
    const active =
      location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
    const showBadge = item.badgeKey === 'warnings' && warningCount > 0
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`flex min-h-[44px] items-center rounded-eh-sm px-3 text-sm transition-colors ${
          active
            ? 'bg-eh-keylime text-eh-forest-ink font-medium'
            : 'text-eh-muted hover:bg-eh-mint/60 hover:text-eh-forest-ink'
        }`}
        title={item.label}
      >
        {compact ? (
          <span className="relative mx-auto font-label text-xs">
            {item.label.slice(0, 1)}
            {showBadge && (
              <span className="absolute -right-2 -top-1 h-2 w-2 rounded-full bg-red-500" />
            )}
          </span>
        ) : (
          <span className="flex w-full items-center justify-between gap-2">
            <span>{item.label}</span>
            {showBadge && (
              <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {warningCount > 99 ? '99+' : warningCount}
              </span>
            )}
          </span>
        )}
      </Link>
    )
  }

  const renderNav = (compact: boolean) => (
    <nav className="space-y-4 overflow-y-auto">
      {adminNavSections.map((section) => (
        <div key={section.title}>
          {!compact && (
            <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-eh-forest-ink/60">
              {section.title}
            </div>
          )}
          <div className="space-y-1">
            {section.items.map((item) => renderNavLink(item, compact))}
          </div>
        </div>
      ))}
    </nav>
  )

  return (
    <div className="bg-eh-cream min-h-[calc(100vh-4rem)]">
      <button
        type="button"
        className="fixed left-3 top-20 z-40 inline-flex h-10 w-10 items-center justify-center rounded-eh-sm bg-eh-cream text-eh-forest-ink shadow-eh-soft border border-eh-border backdrop-blur md:hidden"
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-label="Toggle admin sidebar"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      <aside
        className={`fixed left-0 top-16 z-30 hidden h-[calc(100vh-4rem)] bg-eh-keylime/40 border-r border-eh-border transition-[width] duration-200 md:block ${
          collapsed ? 'w-[72px]' : 'w-[264px]'
        }`}
      >
        <div className="flex h-full flex-col px-3 py-4">
          <div className="mb-4 flex items-center justify-between">
            {!collapsed && (
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-eh-forest-ink">
                Khu vực Admin
              </div>
            )}
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-eh-sm bg-eh-cream text-eh-forest-ink border border-eh-border hover:bg-eh-mint"
              onClick={() => setCollapsed((prev) => !prev)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronDoubleRightIcon className="h-4 w-4" />
              ) : (
                <ChevronDoubleLeftIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          {renderNav(collapsed)}
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close sidebar"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-16 h-[calc(100vh-4rem)] w-[264px] overflow-y-auto bg-eh-cream border-r border-eh-border p-4 shadow-eh-soft">
            <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-eh-forest-ink">
              Khu vực Admin
            </div>
            {renderNav(false)}
          </aside>
        </div>
      )}

      <div className={`transition-[padding] duration-200 ${collapsed ? 'md:pl-[72px]' : 'md:pl-[264px]'}`}>
        <div className="px-4 py-6 sm:px-6 lg:px-8 [&_table]:bg-eh-cream [&_thead]:bg-eh-keylime/70 [&_th]:text-eh-forest-ink">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

export default AdminLayout
